import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  TextField,
  MenuItem,
  FormControl,
} from "@mui/material";
import React, { useEffect, useState, useContext } from "react";
import userContext from '../../../Shared-ui/Context/UserContext';
import CloseIcon from "@mui/icons-material/Close";
import { StartLoading, StopLoading } from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { useDispatch } from "react-redux";
import { ShowError, ShowSuccess } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import { ADMIN_ORGANIZATIONS, ADMIN_USERS } from "../../../Services/Endpoint";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import { Organization } from "../../../Shared-ui/Types/Admin";
import { isOnlyAlphabetsNumbers } from "../../../Shared-ui/utils/Common/Common";
import { User } from "../../../Shared-ui/Types/User";

interface MyComponentProps {
  open: boolean;
  handleClose: () => void;
  organization: Organization | null;
  setOrganization: React.Dispatch<React.SetStateAction<Organization | null>>;
}


const CreateOrganization: React.FC<MyComponentProps> = (props) => {
  const today = new Date();
  const futureDate = new Date();
  futureDate.setDate(today.getDate() + 365);
  const organizationErrInitial = {
    name: { error: "", value: false },
    description: { error: "", value: false },
    max_users: { error: "", value: false },
  }
  const organizationInitial: Organization = {
    name: "",
    description: "",
    subscription: "",
    subscription_status: "active",
    subscription_expires_at: futureDate.toISOString().split("T")[0],
    max_users: undefined,
  }
  // Get current user from context
  const { user } = useContext(userContext);
  const user_role = user?.role;
  const isSuperAdmin = user_role === 'super admin';
  const [organization, setOrganization] = useState({ ...organizationInitial });
  const [organizationErr, setOrganizationErr] = useState(organizationErrInitial);
  const [isEditing, setIsEditing] = useState(false)
  const [users, setUsers] = useState<User[]>([]);
  const [limit, setLimit] = useState(999)
  const [userSearch, setUserSearch] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [unSelectedUsers, setUnSelectedUsers] = useState<string[]>([]);
  const dispatch = useDispatch();


  useEffect(() => {
    getUsers()
  }, [])


  useEffect(() => {
    if (props.open) {
      setUserSearch("");
      setOrganizationErr({ ...organizationErrInitial }); // Always reset errors on open
      if (props.organization && props.organization.id) {
        setOrganization(props.organization)
        getOrganizationUsers(props.organization.id)
        setIsEditing(true)
      } else {
        setOrganization({ ...organizationInitial });
        setSelectedUsers([]);
        setUnSelectedUsers([]);
        setUserSearch("");
        setIsEditing(false);
      }
    }
  }, [props.organization, props.open])


  const getUsers = async () => {
    try {
      dispatch(StartLoading())
      const data = await BaseInstance.get(`${ADMIN_USERS}`).then(res => res?.data)
      data?.items?.sort((a: { created_at: string }, b: { created_at: string }) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setUsers(data?.items || [])
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading())
    }
  }



  const getOrganizationUsers = async (organizationId: string) => {
    try {
      dispatch(StartLoading())
      const data = await BaseInstance.get(`${ADMIN_ORGANIZATIONS}/${organizationId}/users`).then(res => res?.data)
      data.sort((a: { created_at: string }, b: { created_at: string }) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setSelectedUsers(data.map((user: User) => user.id || ""));
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading())
    }
  }


  const addUsersOrganization = async (organizationId: string) => {
    try {
      dispatch(StartLoading())
      const selectedUserIds = {
        user_ids: selectedUsers,
      }
      const data = await BaseInstance.post(`${ADMIN_ORGANIZATIONS}/${organizationId}/users`, selectedUserIds).then(res => res?.data)
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading())
    }
  }

  const deleteUsersOrganization = async (organizationId: string) => {
    try {
      dispatch(StartLoading())
      const unSelectedUserIds = {
        user_ids: unSelectedUsers,
      }
      const data = await BaseInstance.delete(`${ADMIN_ORGANIZATIONS}/${organizationId}/users`, { data: unSelectedUserIds }).then(res => res?.data)
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading())
    }
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    if (name === 'max_users') {
      // Allow only positive integers (no leading zeros, signs, or decimals)
      if (value === '') {
        setOrganization(prev => ({ ...prev, max_users: undefined }));
        return;
      }
      if (!/^[1-9]\d*$/.test(value)) return;
      setOrganization(prev => ({ ...prev, max_users: Number(value) }));
      return;
    }
    setOrganization(prev => ({
        ...prev,
        [name]: value
      }));
  };

  const validateOrganization = () => {
    var error = 0

    const setError = (field: string, condition: boolean, message: string) => {
      if (condition) {
        setOrganizationErr(prev => ({
          ...prev,
          [field]: { error: message, value: true }
        }));
        error += 1;
      } else {
        setOrganizationErr(prev => ({
          ...prev,
          [field]: { error: "", value: false }
        }));
      }
    }

    setError(
      "name",
      !organization.name.trim() ||
        !isOnlyAlphabetsNumbers(organization.name.trim()) ||
        organization.name.trim().length < 2 ||
        !/[a-zA-Z]/.test(organization.name.trim()),
      "Please enter a name with at least 2 characters (A-z and 0-9)"
    );
    setError("description", !organization.description.trim()|| organization.description.trim().length > 500, "Please enter the description A-z (max 500 characters)")

    // Only validate max_users for super admin
    if (isSuperAdmin) {
      const val = Number(organization.max_users);
      setError(
        'max_users',
        organization.max_users === undefined || isNaN(val) || !Number.isInteger(val) || val <= 0,
        'Please enter a valid maximum user limit (integer greater than 0)'
      );
    }

    return error > 0 ? false : true
  }

  const createOrganization = async () => {
    if (!validateOrganization()) return
    try {
      dispatch(StartLoading())
      const data = await BaseInstance.post(`${ADMIN_ORGANIZATIONS}`, organization).then(res => res?.data)
      if (selectedUsers.length > 0) {
        await addUsersOrganization(data.id);
      }
      if (unSelectedUsers.length > 0) {
        await deleteUsersOrganization(data.id);
      }
      handleClose()
      dispatch(ShowSuccess("Organization created successfully"));
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading())
    }
  };

  const filteredUsers = users.filter(user =>
    user.full_name.toLowerCase().includes(userSearch.toLowerCase())
  );

  const handleUserCheck = (userId: string) => {
    if (userId) {
      setSelectedUsers(prev => {
        if (prev.includes(userId)) {
          // If unchecked, add to unSelectedUsers
          setUnSelectedUsers(unPrev => [...unPrev, userId]);
          return prev.filter(uid => uid !== userId);
        } else {
          // If checked, remove from unSelectedUsers if present
          setUnSelectedUsers(unPrev => unPrev.filter(uid => uid !== userId));
          return [...prev, userId];
        }
      });
    }
  };

  const updateOrganization = async () => {
    if (!validateOrganization()) return;
    try {
      dispatch(StartLoading())
      const data = await BaseInstance.put(`${ADMIN_ORGANIZATIONS}/${organization.id}`, organization).then(res => res?.data)
      dispatch(ShowSuccess("Organization updated successfully"));
      if (selectedUsers.length > 0) {
        await addUsersOrganization(data.id);
      }
      if (unSelectedUsers.length > 0) {
        await deleteUsersOrganization(data.id);
      }
      handleClose()
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.detail));
    } finally {
      dispatch(StopLoading())
    }
  };

  const handleClose = () => {
    setOrganization(organizationInitial)
    setOrganizationErr({ ...organizationErrInitial });
    props.setOrganization(null)
    setIsEditing(false)
    props.handleClose()
  }

  return (
    <Dialog open={props.open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle
        sx={{
          fontWeight: 600,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
        }}
      >
        <Box>
          {!isEditing ? "Create" : "Update"} Organization
          <Typography
            variant="subtitle2"
            color="textSecondary"
            sx={{ mt: 0.5 }}
          >
            {!isEditing ? "Add a new organization to the system" : "Edit the organization details"}
          </Typography>
        </Box>
        <IconButton onClick={props.handleClose} sx={{ ml: 1 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box
          component="form"
          // onSubmit={handleSubmit}
          sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}
        >
          <FormControl fullWidth variant="outlined" sx={{ mb: 1 }} error={organizationErr.name.value}>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={organization.name}
              onChange={handleChange}
              placeholder="Organization name"
              sx={{ mt: 1 }}
              error={organizationErr.name.value}
              helperText={organizationErr.name.error}
              inputProps={{ maxLength: 50 }}
            />
          </FormControl>

          <FormControl fullWidth variant="outlined" sx={{ mb: 1 }} error={organizationErr.description.value}>
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={organization.description}
              onChange={handleChange}
              placeholder="Organization description"
              multiline
              rows={4}
              inputProps={{ maxLength: 500 }} 
              error={organizationErr.description.value}
              helperText={organizationErr.description.error}
            />
             <Box display="flex" justifyContent="flex-end">
                          <Typography variant="caption" color="textSecondary" align="right">
                            {`${organization.description.length} / ${500} characters`}
                          </Typography>
                        </Box>
          </FormControl>

        {/* Only show for super admin, after description and before subscription status */}

          {isSuperAdmin && (
            <FormControl fullWidth variant="outlined" sx={{ mb: 1 }} error={organizationErr.max_users.value}>
              <TextField
                fullWidth
                label="Maximum User Limit"
                name="max_users"
                type="text"
                value={organization.max_users === undefined ? '' : organization.max_users}
                onChange={handleChange}
                placeholder="Enter maximum users"
                error={organizationErr.max_users.value}
                helperText={organizationErr.max_users.error}
                inputProps={{ inputMode: 'numeric', pattern: '[1-9][0-9]*' }}
                required
              />
            </FormControl>
          )}

          <FormControl fullWidth variant="outlined" sx={{ mb: 1 }}>
            <TextField
              fullWidth
              label="Subscription Status"
              name="subscription_status"
              value={organization.subscription_status}
              onChange={handleChange}
              placeholder="active"
              select
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
            </TextField>
          </FormControl>


          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              p: 2,
              mt: 2,
              bgcolor: "background.paper",
              maxHeight: 320, // Set a max height for the whole box
              overflow: "auto", // Enable scrolling for the box
            }}
          >
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Assign Users
            </Typography>
            <TextField
              fullWidth
              label="Search Users"
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              placeholder="Search by name"
              sx={{ mb: 2 }}
            />
            <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
              {filteredUsers.map((user) => (
                user.id && (
                  <Box key={user.id} sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user.id)}
                      onChange={() => handleUserCheck(user.id || "")}
                      id={`user-checkbox-${user.id}`}
                    />
                    <label htmlFor={`user-checkbox-${user.id}`} style={{ marginLeft: 8 }}>
                      {user.full_name}
                    </label>
                  </Box>
                )
              ))}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={() => handleClose()} color="primary" variant="outlined"  size="large"  sx={{textTransform: "capitalize",}}>
          Cancel
        </Button>
        <Button variant="contained" color="primary" type="submit"  size="large"  sx={{textTransform: "capitalize",}}
          onClick={!isEditing ? () => createOrganization() : () => updateOrganization()}
        >
          {!isEditing ? "Create" : "Update"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateOrganization;